/** Marketing consent (v4 marketing-notification-preferences). Mirrors
 * signup-form-lib's SeekrConsentServiceV2. The category endpoint is public;
 * update/ requires auth and runs after complete-signup logs the new user in,
 * so the session cookie is present by then.
 *
 * A service instance caches the category (purpose + statement) across the
 * calls one mounted flow makes.
 */

import type { Client } from "./client";
import { ApiHttpError } from "./errors";
import type { MarketingPurpose } from "./types";

export interface ConsentService {
  /** The purpose definition, or null if it can't be fetched (non-fatal: the
   * consent checkbox keeps its static fallback label). */
  loadPurpose(): Promise<MarketingPurpose | null>;
  /** Whether this user holds a live marketing subscription. An earlier
   * unsubscribe is not one — they get asked again. Throws on HTTP errors —
   * callers decide whether that blocks anything. */
  isSubscribedToMarketing(email: string): Promise<boolean>;
  /** Record the user's choice. Throws on HTTP failure (callers decide
   * fatality). `formContext` names where they saw the checkbox for the audit
   * trail; `opts.variantKey` is noted in the audit reason when supplied.
   *
   * The shown variant's legal basis is sent from the loaded category, so every
   * screen records it without having to remember to (the backend still guards
   * it against the catalog). `opts.legalBasis` overrides that. */
  save(
    optedIn: boolean,
    formContext: string,
    opts?: { variantKey?: string; legalBasis?: string },
  ): Promise<void>;
}

export function createConsentService(client: Client): ConsentService {
  let purpose: MarketingPurpose | null = null;
  let statement = "";

  async function loadPurpose(): Promise<MarketingPurpose | null> {
    if (purpose) return purpose;
    try {
      const res = await client.get(`${client.consentBase}/category/`);
      const data = (await res.json()) as {
        title?: string;
        consent_statement?: string;
        default?: boolean;
        name?: string;
        legal_basis?: string;
      };
      statement = data.consent_statement ?? "";
      purpose = {
        title: data.title,
        consent_statement: data.consent_statement,
        granted_by_default: data.default,
        name: data.name,
        legal_basis: data.legal_basis,
      };
      return purpose;
    } catch {
      return null;
    }
  }

  return {
    loadPurpose,

    async isSubscribedToMarketing(email) {
      const params = new URLSearchParams({ email });
      const res = await client.get(
        `${client.consentBase}/any-specified/?${params}`,
      );
      // An HTTP error must throw, not be mistaken for a "no" and re-prompt the
      // user.
      if (!res.ok) throw new Error("consent check failed");
      // The path and key predate the rule they now answer: upstream reads them
      // as "subscribed to any tracked marketing id".
      const data = (await res.json()) as { any_specified?: boolean };
      return !!data.any_specified;
    },

    /** `source` stays "Signup" for both contexts: SourceChoices has no
     * sign-in value (an unknown source is a 400), it's what the old flow's
     * post-signin consent form sends, and it keeps the backend's
     * ignore-unchecked-box opt-out guard for Hubspot-sourced categories.
     *
     * Throws on HTTP failure — the caller decides whether that's fatal (the
     * existing-user step surfaces it; the new-user step treats it as
     * non-fatal, since the account already exists). */
    async save(optedIn, formContext, opts) {
      // Also settles the legal basis: it comes off the same cached category as
      // the statement, so no caller has to carry it back here by hand (one
      // did, the other didn't, and the two screens drifted apart).
      await loadPurpose();
      const legalBasis = opts?.legalBasis ?? purpose?.legal_basis;
      const variantNote = opts?.variantKey ? ` (variant: ${opts.variantKey})` : "";
      const reasonDetails = optedIn
        ? `User saw and agreed to the consent statement: ${statement}${variantNote}`
        : `User did not check the checkbox ${formContext}. They saw: ${statement}${variantNote}`;
      const res = await client.post(`${client.consentBase}/update/`, {
        opted_in: optedIn,
        reason_details: reasonDetails,
        source: "Signup",
        ...(legalBasis ? { legal_basis: legalBasis } : {}),
      });
      // User-safe message, per the api/ convention (see auth.ts): the
      // existing-user consent step shows err.message verbatim, so a
      // technical "consent update failed (500)" here reached visitors.
      //
      // ApiHttpError, not Error: the message is for the visitor, the status is
      // for us. Reporting a failed write without it says only "it broke" —
      // which is what made a 403 (a missing CSRF_TRUSTED_ORIGINS entry on the
      // consumer origin) indistinguishable from the upstream being down.
      if (!res.ok) {
        throw new ApiHttpError(
          res.status,
          "Couldn't save your preference. Please try again.",
        );
      }
    },
  };
}
